import { useEffect, useRef } from 'react'
import { useScroll, useScrolling, useToggle } from 'react-use'
import { jsonStringify } from '@ztechapps/common.utils'
import type { UseListResult } from '../useList'

export type useLoadMoreOnScrollProps = UseListResult

/**
 * Calls loadMoreEntries when the user scrolls to the bottom of the table
 */
export const useLoadMoreOnScroll = ({ loading, loadMoreEntries, pageInfo, list, filter, sort }: useLoadMoreOnScrollProps) => {
  //this is the reference to the element that we are tracking for scroll
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrolledOnce, setScrolledOnce] = useToggle(false)

  //we use the useScroll from the react-use library because of tracking animation, and it has hooks on scroll
  //the value of y is in px of scrollTop
  const { y } = useScroll(scrollRef)
  const scrolling = useScrolling(scrollRef)
  const pageCount = pageInfo?.count ?? 0
  const listCount = list?.length ?? 0
  const hasMore = listCount > 0 && pageCount > 0 && pageCount > listCount

  //we set the flag that the user scrolled here once
  useEffect(() => {
    if (!scrolling) return
    setScrolledOnce(true)
  }, [scrolling])

  const { scrollHeight, clientHeight } = scrollRef?.current ?? {}
  const scrolledHeight = Math.ceil(y + clientHeight) + 3 //we add a buffer value to consider decimal value that is rounded

  useEffect(() => {
    if (!hasMore) return
    if (loading) return
    if (scrolledHeight < scrollHeight) return
    if (!scrolledOnce) return

    console.debug(
      'LoadMore (when all are true), because of hasMore:',
      hasMore,

      'not loading:',
      !loading,

      'not scrolledHeight < scrollHeight:',
      !(scrolledHeight < scrollHeight),

      'scrolledOnce:',
      scrolledOnce,

      'listCount:',
      listCount,

      'pageCount:',
      pageCount,

      'scrolledHeight:',
      scrolledHeight,

      'scrollHeight:',
      scrollHeight
    )

    //load the remaining entries if the above conditions passed
    const searchString = jsonStringify(filter)
    loadMoreEntries(listCount, searchString, sort)

    setScrolledOnce(false) //reset the scrolled state
  }, [loading, scrolledHeight, scrollHeight, scrolledOnce, hasMore, pageCount, listCount])

  return {
    scrollRef,
    hasMore,
  }
}
